// @flow
import addResourceAnnotations from 'app/packages/trace-logger/utils/addResourceAnnotations';
import context from 'app/common/lib/context';
import DomUtil from 'app/common/lib/DomUtil';
import getFilename from 'app/packages/trace-logger/utils/getFilename';
import getGridItemImageUrls from 'app/common/lib/TraceLogger/gridHandler/getGridItemImageUrls';
import Stopwatch from 'app/packages/trace-logger/Stopwatch';
import tracingElementSelector from 'app/packages/trace-logger/utils/tracingElementSelector';
import { DEBUG_ENABLED } from 'app/packages/trace-logger/debugFlags';
import { getEntries } from 'app/packages/trace-logger/resourceTiming';
import type {
  RegisterGridImagesTraceEvent,
  RegisterGridItemsPositionedTraceEvent,
  CustomTraceEvent,
  StopTraceEvent,
} from 'app/common/lib/TraceLogger/types';
import type { GridHandlerOptions, GridHandler } from 'app/common/lib/TraceLogger/gridHandler/types';

const MAX_RETRY_COUNT = 2;

export default function legacyGridHandler({
  timerExists,
  pageLoad,
}: GridHandlerOptions): GridHandler {
  let _normalizedImageDataMap: {
    [url: string]: {
      resourceTimingObject: Object,
      el: ?HTMLElement,
      stopwatchAdded?: boolean,
    },
  } = {};

  const _registeredImageTypes: { [url: string]: 'pin' | 'board' | 'interest' } = {};
  let _stillRegisteringImages: boolean = true;

  /**
   * Takes an array of normalized data and registers and starts stopwatches for
   * each one. Each stopwatch is a subStopwatch of this handler's instance.
   */
  const createSubStopwatchesFromNormalizedData = (timer: Stopwatch) => {
    // For each element in the normalized data, we extract what we need to start stopwatches for
    // each one. These stopwatches will hold the state of each image.
    // console.log('NORMALIZED DATA', _normalizedImageDataMap);
    Object.keys(_normalizedImageDataMap)
      .filter((key) => {
        const imageData = _normalizedImageDataMap[key];
        DomUtil.createViewportBoundingBox();
        return DomUtil.isElementInBoundingBox(imageData.el);
      })
      .forEach((key, index) => {
        const imageData = _normalizedImageDataMap[key];
        if (imageData && !imageData.stopwatchAdded) {
          const { resourceTimingObject, el: element } = imageData;
          const subTimer = timer.addSubStopwatch(
            new Stopwatch({
              name: 'image',
              metricName: timer.metricName,
              coexistId: key,
              startTime: resourceTimingObject
                ? window.performance.timing.navigationStart + resourceTimingObject.startTime
                : undefined,
            }),
          );
          imageData.stopwatchAdded = true;

          // If we're in debug mode, also add some style to we can easily see which images are
          // counted in this timing
          if (DEBUG_ENABLED && element) {
            element.style.outline = '10px solid red';
            element.style.outlineOffset = '-30px';
          }

          // Now that this image has been added to the current timer as a submetrics, check on it
          // until either it has an endtime or the retries have been exhausted.
          const MAX_IMG_RESOURCE_ATTEMPTS = 5;
          const IMG_RESOURCE_POLL_INTERVAL = 2000; // poll every 2s
          let attemptCount = 0;
          const checkImageIntervalId = setInterval(() => {
            if (subTimer.endTime) {
              clearInterval(checkImageIntervalId);
              return;
            }
            attemptCount += 1;
            const { resourceTimingObject } = imageData;
            const { responseEnd } = resourceTimingObject;
            if (responseEnd) {
              addResourceAnnotations(subTimer, resourceTimingObject);
              subTimer.endTime = window.performance.timing.navigationStart + responseEnd;
            }
            if (responseEnd || attemptCount === MAX_IMG_RESOURCE_ATTEMPTS) {
              clearInterval(checkImageIntervalId);
            }
          }, IMG_RESOURCE_POLL_INTERVAL);
        }
      });
    // And signal to the stopwatch that we're done processing
    // TODO(jchan): maybe make this event-based instead of polling
    timer.isProcessing = false;
  };

  /**
   * This handler kicks off the logic that determines which images are in the first page load
   * and registers stopwatches for each one as submetrics of the current timer.
   *
   * This is applicable to both user nav and page load timings that load grid images.
   */
  const handleRegisterGridImages = (traceEvent: RegisterGridImagesTraceEvent) => {
    // If we're done registering images, bounce this event out
    if (!_stillRegisteringImages) {
      return;
    }

    if (traceEvent.el && !DomUtil.isAnyPartOfElementVisibleInViewport(traceEvent.el)) {
      return;
    }

    // If the viewport is filled, we're no longer registering images
    _stillRegisteringImages = !traceEvent.viewportFilled;

    const devicePixelRatio = window.devicePixelRatio;
    const isRetina = context.instance.is_retina;

    // Takes grid data (props.items) from the Masonry grid and stores it for later reconciliation
    // Stores registered images in an array of image filenames to be reconciled later
    traceEvent.gridData.forEach((item) => {
      getGridItemImageUrls({ item, devicePixelRatio, isRetina })
        .map((url) => getFilename(url))
        .filter(Boolean)
        .forEach((filename) => {
          _registeredImageTypes[filename] = item.type;
        });
    });
  };

  /**
   * At this point we know that all the grid items have been registered, normalized, and positioned.
   * We can now extract image data and populate substopwatches for them.
   *
   * For page loads, we use the window's `onLoad` event to signal that the grid items in
   * Masonry have been positioned (see TraceLogger/utils.js). There may be a better way
   * to tell this in the future.
   *
   * NOTE: While it seems like this is always called before the stop timer event, the stop timer
   * event can actually called independently of this event (for abort reasons, etc) so they can't
   * be collapsed.
   *
   * At this point client-side events have registered images fetched and we are now going to fill in
   * _normalizedImageDataMap with all the image loading info we have.
   * We may have also begun to populate the _normalizedImageDataMap already, so if there is already
   * an entry for a specific image, we will try to fill in the missing pieces.
   *
   * We try to get data from window.performance.timing for every image that is registered. We retry
   * MAX_RETRY_COUNT number of times because at the time this event is first fired, the entries
   * may not be ready with the data that we need. This will keep track of retries and keep checking
   * every 2 seconds until we've exhausted the retries or gotten data for all the images.
   */
  const handleGridItemsPositioned = (
    traceEvent: RegisterGridItemsPositionedTraceEvent,
    timer: Stopwatch,
  ) => {
    timer.isProcessing = true;
    timer.addAnnotation(traceEvent.name);
    const retryMap = {};
    const checkResourceTimingAPIInterval = setInterval(() => {
      // If this stopwatch has been stopped, clear this interval and don't do anything else.
      if (!timerExists()) {
        clearInterval(checkResourceTimingAPIInterval);
        return;
      }
      const urlsToRemove = [];
      Object.keys(_registeredImageTypes).forEach((url) => {
        const resourceTimingObject =
          getEntries().find((entry) => entry.name.includes(url)) ||
          (_normalizedImageDataMap[url] || {}).resourceTimingObject;

        // If there is a resourceTimingObject object OR there is an existing entry and it already has a resourceTimingObject
        // object, we can enter this block.
        // Sometimes `window.performance.getEntriesByType()` comes up empty because there are som
        // window.performance.clearPerformanceData() is called in other places. :(
        if (resourceTimingObject) {
          _normalizedImageDataMap[url] = {
            resourceTimingObject,
            el: tracingElementSelector(resourceTimingObject.name, _registeredImageTypes[url]),
          };
          urlsToRemove.push(url); // because we've got what we needed
        } else {
          retryMap[url] = (retryMap[url] || 0) + 1; // retry
          if (retryMap[url] === MAX_RETRY_COUNT) {
            urlsToRemove.push(url); // because we're giving up on it
          }
        }
      });
      urlsToRemove.forEach((url) => {
        delete _registeredImageTypes[url];
      });
      if (!Object.keys(_registeredImageTypes).length) {
        clearInterval(checkResourceTimingAPIInterval);
        createSubStopwatchesFromNormalizedData(timer);
      }
    }, 2000);
  };

  return {
    dispatch: (event: CustomTraceEvent | StopTraceEvent, timer: Stopwatch) => {
      if (
        (pageLoad && event.name === 'PageLoadRegisterGridImagesTraceEvent') ||
        (!pageLoad && event.name === 'UserNavRegisterGridImagesTraceEvent')
      ) {
        handleRegisterGridImages(event);
        return true;
      }
      if (
        (pageLoad && event.name === 'PageLoadRegisterGridItemsPositionedTraceEvent') ||
        (!pageLoad && event.name === 'UserNavRegisterGridItemsPositionedTraceEvent')
      ) {
        handleGridItemsPositioned(event, timer);
        return true;
      }
      return false;
    },
    cleanup: () => {
      _normalizedImageDataMap = {};
      _stillRegisteringImages = true;
    },
  };
}
